import { useState, useCallback } from "react";

export function useFormValidation() {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validate = useCallback((field, value, rule) => {
    if (!rule) return null;

    // Required
    if (rule.required) {
      if (value === undefined || value === null || value === "") return rule.message || "Este campo es obligatorio";
      if (typeof value === "string" && value.trim() === "") return rule.message || "Este campo es obligatorio";
      if (Array.isArray(value) && value.length === 0) return rule.message || "Selecciona al menos una opción";
    }

    // Custom validator - run BEFORE early return to support cross-field validation
    // (e.g., tiempo required only when días > 0)
    if (rule.custom) {
      const customError = rule.custom(value);
      if (customError) return customError;
    }

    // Skip further checks if empty and not required
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "string" && value.trim() === "") return null;

    // Numeric range
    const numVal = Number(value);
    if (rule.min !== undefined && !isNaN(numVal) && numVal < rule.min) {
      return rule.message || `El valor mínimo es ${rule.min}`;
    }
    if (rule.max !== undefined && !isNaN(numVal) && numVal > rule.max) {
      return rule.message || `El valor máximo es ${rule.max}`;
    }

    // Min length
    if (rule.minLength && typeof value === "string" && value.trim().length < rule.minLength) {
      return rule.message || `Mínimo ${rule.minLength} caracteres`;
    }

    // Pattern
    if (rule.pattern && typeof value === "string" && !rule.pattern.test(value)) {
      return rule.message || "Formato inválido";
    }

    return null;
  }, []);

  // Re-validate a field on value change (clears errors in real-time)
  const onFieldChange = useCallback((field, value, rule) => {
    if (touched[field]) {
      const error = validate(field, value, rule);
      setErrors((prev) => ({ ...prev, [field]: error || undefined }));
    }
  }, [touched, validate]);

  // Validate a single field and update state
  const validateField = useCallback((field, value, rule) => {
    const error = validate(field, value, rule);
    setErrors((prev) => ({ ...prev, [field]: error }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    return error;
  }, [validate]);

  // Validate all fields in a rules object against data, return errors
  const validateStep = useCallback((rules, data) => {
    const newErrors = {};
    Object.entries(rules).forEach(([field, rule]) => {
      const error = validate(field, data[field], rule);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    // Mark all rule fields as touched
    setTouched((prev) => {
      const next = { ...prev };
      Object.keys(rules).forEach((k) => { next[k] = true; });
      return next;
    });
    return newErrors;
  }, [validate]);

  // Get error only if field was touched
  const getError = useCallback((field) => {
    return touched[field] ? errors[field] || null : null;
  }, [touched, errors]);

  // Check if step is valid without setting state
  const isStepValid = useCallback((rules, data) => {
    return Object.entries(rules).every(([field, rule]) => {
      return !validate(field, data[field], rule);
    });
  }, [validate]);

  const clearErrors = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  return { validateField, validateStep, getError, isStepValid, errors, touched, clearErrors, onFieldChange };
}
